import { useEffect, useState } from "react";
import { Button, Card, Divider, Form, message, Typography, Upload } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { BasicUserInfo, DocumentUpload } from '@operations/shared';
import { request } from '../../apis';
import { errorMessage } from '../../utils/commonStringsConsts';
import { stepperMoving } from '../../stores/actions';
import { getRegion } from '../../components/regionWrapper';
import { Region } from "../../utils/commonInterfaces";

interface userInfoTypes {
    name: string,
    staff_id: number,
    companyName: string,
    userID: number,
    phone: string,
    jobTitle: string,
    email: string,
    userStatusType: string,
    hrLetter: [string?],
    nationalId: [string?, string?],
    consent: [string?],
    trustReceipt: { id: number, url: string }[]
}
type DocFieldKey = "hrLetter" | "nationalIdFront" | "nationalIdBack" | "consent" | "trustReceipt"
export interface UserDocsFormProps {
    apiLink: string;
}
export default function UserDocsForm({ apiLink }: UserDocsFormProps) {
    const dispatch = useDispatch()
    const userInfo = useSelector<{ [key: string]: any }, userInfoTypes>(state => state.actionsReducer.info)
    const region = getRegion()
    const [userFiles, setUserFiles] = useState<{ [key: string]: any }>({
        hrLetter: "",
        nationalIdFront: "",
        nationalIdBack: "",
        consent: "",
        trustReceipt: [],
        userID: ""
    })
    const [sending, setSending] = useState(false)
    useEffect(() => {
        setUserFiles({
            hrLetter: userInfo.hrLetter[0] ? userInfo.hrLetter[0] : "",
            nationalIdFront: userInfo.nationalId[0] ? userInfo.nationalId[0] : "",
            nationalIdBack: userInfo.nationalId[1] ? userInfo.nationalId[1] : "",
            consent: userInfo.consent[0] ? userInfo.consent[0] : "",
            ...(region === Region.EG && { trustReceipt: userInfo.trustReceipt ? userInfo.trustReceipt.map((file: { id: number, url: string }) => ({
                ...file,
                name: file.url?.split('/').pop()
            })) : [] }),
            userID: userInfo.userID
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo])
    const checkDocsChanged = () => {
        let updatedDocs: string[] = []
        if (userInfo) {
            if (userFiles.consent !== userInfo.consent[0]) {
                updatedDocs.push("consent")
            }
            if (userFiles.hrLetter !== userInfo.hrLetter[0]) {
                updatedDocs.push("hrLetter")
            }
            if ((userFiles.nationalIdFront !== userInfo.nationalId[0]) && !updatedDocs.includes("nationalID")) {
                updatedDocs.push("nationalID")
            }
            if ((userFiles.nationalIdBack !== userInfo.nationalId[1]) && !updatedDocs.includes("nationalID")) {
                updatedDocs.push("nationalID")
            }
            if (region === Region.EG) {
                if (userFiles.trustReceipt[0]?.id !== userInfo.trustReceipt[0]?.id) {
                    updatedDocs.push("trustReceipt")
                }
            }
        }
        return updatedDocs;
    }
    const handleUploadChange = (fieldKey: DocFieldKey, info: any) => {
        let fileList = info?.fileList ?? []
        fileList = fileList.map((file: any) => {
            if (!file.url && file.originFileObj) {
                return { ...file, url: URL.createObjectURL(file.originFileObj) }
            }
            return file
        })

        setUserFiles((prev: any) => ({ ...prev, [fieldKey]: fileList }))
        return fileList
    }
    const trustReceiptBeforeUpload = (file: { size: any, name: string; }) => {
        let file_size = file.size / (1024 * 1024), maxSize = 2, size;
        if (file.size < 1024) {
            size = file.size + ' B';
        } else if (file.size < (1024 * 1024)) {
            size = Math.floor(file.size / 1024) + ' KB';
        } else {
            size = Math.floor(file.size / (1024 * 1024)) + ' MB';
        }
        let acceptable_exe = /(\.png|\.jpg|\.jpeg|\.pdf)$/i;
        if (!acceptable_exe.exec(file.name)) {
            message.error(`The extension of (${file.name}) is not supported!`);
            return Upload.LIST_IGNORE
        } else if (file_size > maxSize) {
            message.error(`File size = ${size} exceeds ${maxSize} MB`);
            return Upload.LIST_IGNORE
        } else {
            return true
        }
    };

    const updateUserDocs = async () => {
        const formData = new FormData()
        formData.append("userID", userFiles.userID ?? "")
        formData.append("updatedDocs", checkDocsChanged().length > 0 ? checkDocsChanged().join(",") : '[" "]')
        const { userID, ...rest } = userFiles
        Object.keys(rest).forEach((key) => {
            const value = rest[key]
            if (typeof value === "string") {
                formData.append(key, value)
                return
            } else if (!value.length) {
                formData.append(key, "")
                return
            }
            else {
                value.forEach((file: any) => {
                    const rawFile = file?.originFileObj
                    if (rawFile instanceof File) {
                        formData.append(key, rawFile, rawFile.name)
                    } else {
                        formData.append(key, file.url)
                    }
                })
            }
        })
        setSending(true)
        try {
            const res: { [key: string]: any } = await dispatch(request({
                url: apiLink, method: "patch", data: formData
            }))
            if (res.data.status === 200) {
                dispatch(stepperMoving(0))
                message.success(res.data.message, 4)
            }
            else {
                message.error(res.data.message, 4)
            }
            setSending(false)
        }
        catch (error: any) {
            setSending(false)
            message.error(errorMessage, 4)
        }
    }

    const renderDocRow = (
        title: string,
        fieldKey: Exclude<DocFieldKey, "trustReceipt">
    ) => (
        <DocumentUpload
            key={fieldKey}
            title={title}
            name={fieldKey}
            value={userFiles[fieldKey]}
            onChange={(info) => handleUploadChange(fieldKey, info)}
        />
    )

    return (
        <div className="uploadUserDocs">
            <Card className="uploadUserDocs-userCard">
                <BasicUserInfo info={userInfo} />
            </Card>

            <Form onFinish={updateUserDocs} layout="vertical">
                <section className="uploadUserDocs-section">
                    <Typography.Title level={5} className="uploadUserDocs-sectionTitle">Identity & consent documents</Typography.Title>
                    <Typography.Text type="secondary" className="uploadUserDocs-sectionDesc">One document per type. Image files only.</Typography.Text>
                    <div className="docUpload-list docUpload-list--grid">
                        {renderDocRow("HR Letter", "hrLetter")}
                        {renderDocRow("Consent", "consent")}
                        {renderDocRow("Front of National ID", "nationalIdFront")}
                        {renderDocRow("Back of National ID", "nationalIdBack")}
                    </div>
                </section>

                {region === Region.EG && (
                <section className="uploadUserDocs-section uploadUserDocs-section--trust">
                    <Typography.Title level={5} className="uploadUserDocs-sectionTitle">Trust receipt</Typography.Title>
                    <Typography.Text type="secondary" className="uploadUserDocs-sectionDesc">PDF or image, max 2MB.</Typography.Text>
                    <div className="docUpload-list">
                        <DocumentUpload
                            title="Trust receipt"
                            hint="PDF or image"
                            name="trustReceipt"
                            accept=".pdf,.jpg,.jpeg,.png"
                            value={userFiles.trustReceipt}
                            onChange={(info) => handleUploadChange("trustReceipt", info)}
                            uploadProps={{ beforeUpload: trustReceiptBeforeUpload }}
                        />
                    </div>
                </section>
                )}

                <Divider className="uploadUserDocs-divider" />
                <Form.Item className="uploadUserDocs-submit" style={{ marginBottom: 0 }}>
                    <Button type="primary" htmlType="submit" loading={sending} size="large">
                        Update documents
                    </Button>
                </Form.Item>
            </Form>

        </div>
    )
}